import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { WithHiddenListBox, WithNavigationIcon } from '@components/design';
import { elements } from '@ecosystem/newron-design-system';
import {
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
  useGenerateChartInteractionTrackingParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { ComponentName } from '@models/BrandDependency';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

interface Props {
  viewingBrand: string;
  setViewingBrand: (brand: string, source?: 'restore' | 'kpi_selector' | 'brand_selector') => void;
  setIsloading: Dispatch<SetStateAction<boolean>>;
  brands?: string[];
}

const BrandDeepDiveBrandSelector: React.FC<Props> = ({ viewingBrand, setViewingBrand, brands = [] }) => {
  const chartInteractionParam = useGenerateChartInteractionTrackingParam('marketPremiumRetailerClient');
  const [selectedBrand, setSelectedBrand] = useState<string>(viewingBrand);
  const findBrandIndex = (brandValue: string | null = null) =>
    brands.findIndex((brand) => {
      return brandValue === null ? brand === viewingBrand : brand === brandValue;
    });
  const [brandIndex, setBrandIndex] = useState<number>(() => {
    const idx = brands.findIndex((brand) => brand === viewingBrand);
    return idx < 0 ? 0 : idx;
  });
  const { Icon } = elements;
  const { ChevronLeft, ChevronRight } = Icon.icons;
  const pagesConfig = usePageConfig();

  const brandOptions = brands.map((brand) => ({
    label: brand,
    value: brand,
  }));

  const isFirstBrand = brandIndex === 0;

  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [fromNavigation, setFromNavigation] = useState<boolean>(false);

  const isLastBrand = brandIndex === brands.length - 1;

  const onChangeTracking = (label: string) => {
    if (label && !isFirstTime) {
      const trackingParam: ChartInteractionTrackingFunctionParam = {
        ...chartInteractionParam,
        pageType: PageType.OPPORTUNITY_REVIEW,
        pageName: PageName.TRADING_ASSISTANT,
        elementName: ChartElementName.BRAND,
        elementType: ChartElementType.FILTER,
        optionChosen: label,
        chartName: ComponentName.RETAILER_MARKET_RELATIONSHIP_BUTTERFLY_CHART,
        access: pagesConfig.access,
      };
      chartInteractionTrackingFunction(trackingParam);
    }
  };

  // Sync selectedBrand with viewingBrand whenever viewingBrand changes externally
  useEffect(() => {
    if (viewingBrand !== selectedBrand) {
      setSelectedBrand(viewingBrand);
    }
  }, [viewingBrand]);

  useEffect(() => {
    const idx = findBrandIndex();
    setBrandIndex(idx < 0 ? 0 : idx);
  }, [brands, viewingBrand]);

  // Handle selectedBrand changes to update viewingBrand and brandIndex
  useEffect(() => {
    const selectedOption = brandOptions.find((o) => o.value === selectedBrand);
    if (selectedOption && selectedOption?.value !== viewingBrand) {
      setViewingBrand(selectedBrand, 'brand_selector');
      setBrandIndex(findBrandIndex(selectedOption?.value));
      onChangeTracking(selectedOption?.value);
    }
  }, [selectedBrand]);

  // Handle first load or navigation updates (arrow buttons)
  useEffect(() => {
    if (isFirstTime || fromNavigation) {
      setSelectedBrand(brands[brandIndex]);
      setViewingBrand(brands[brandIndex]);
      onChangeTracking(brands[brandIndex]);
      setIsFirstTime(false);
      setFromNavigation(false);
    }
  }, [brandIndex, fromNavigation, isFirstTime]);

  return (
    <div className="flex items-center justify-center mt-2" data-test-id="brand-deep-dive-brand-selector">
      <WithNavigationIcon
        icon={<ChevronLeft />}
        iconColor={() => (isFirstBrand ? 'default' : 'brand')}
        onClick={() => {
          if (!isFirstBrand) {
            setFromNavigation(true);
            setBrandIndex((oldBrandIndex) => oldBrandIndex - 1);
          }
        }}
        testId="brand-selector-left-arrow"
      />
      <WithHiddenListBox
        label={''}
        options={brandOptions}
        value={selectedBrand}
        onChange={(brand) => {
          setSelectedBrand(brand.label);
        }}
        combobox={true}
      />
      <WithNavigationIcon
        icon={<ChevronRight />}
        iconColor={() => (isLastBrand ? 'default' : 'brand')}
        onClick={() => {
          if (!isLastBrand) {
            setFromNavigation(true);
            setBrandIndex((oldBrandIndex) => oldBrandIndex + 1);
          }
        }}
        testId="brand-selector-right-arrow"
      />
    </div>
  );
};

export default BrandDeepDiveBrandSelector;
